import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useReactToPrint } from 'react-to-print';
import { ArrowLeft, Calendar, Gauge, Fuel, Settings2, Check, ChevronLeft, ChevronRight, Printer, X, ZoomIn, Share2 } from 'lucide-react';
import Header from '../components/Header';
import ContactForm from '../components/ContactForm';
import ShareCarCard from '../components/ShareCarCard';
import { Button } from '../components/ui/button';
import { useSettings } from '../context/SettingsContext';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Compact Print Layout
const PrintContent = React.forwardRef(({ car, settings }, ref) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  };
  const formatMileage = (mileage) => new Intl.NumberFormat('pt-PT').format(mileage);

  if (!car) return null;
  const mainImage = resolveImageUrl(car.images?.[0]) || 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400';

  return (
    <div ref={ref} style={{ width: '210mm', height: '297mm', padding: '10mm', background: 'white', color: 'black', fontFamily: 'Arial, sans-serif', boxSizing: 'border-box', position: 'relative' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '5mm', borderBottom: '2px solid #DC2626', marginBottom: '5mm' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <svg width="35" height="35" viewBox="0 0 64 64" fill="none"><circle cx="32" cy="32" r="30" stroke="#DC2626" strokeWidth="1.5" fill="none"/><rect x="16" y="16" width="10" height="20" rx="2" stroke="#DC2626" strokeWidth="1.5" fill="none"/><line x1="21" y1="36" x2="21" y2="48" stroke="#DC2626" strokeWidth="1.5"/><circle cx="21" cy="48" r="3" stroke="#DC2626" strokeWidth="1.5" fill="none"/><rect x="38" y="16" width="10" height="20" rx="2" stroke="#DC2626" strokeWidth="1.5" fill="none"/><line x1="43" y1="36" x2="43" y2="48" stroke="#DC2626" strokeWidth="1.5"/><circle cx="43" cy="48" r="3" stroke="#DC2626" strokeWidth="1.5" fill="none"/><path d="M21 48 L32 42 L43 48" stroke="#DC2626" strokeWidth="1.5" fill="none"/></svg>
          <div>
            <div style={{ fontSize: '18px', fontWeight: '800', color: '#DC2626' }}>ICARDETAIL</div>
            <div style={{ fontSize: '8px', letterSpacing: '2px', color: '#666' }}>AUTOMÓVEIS</div>
          </div>
        </div>
        <div style={{ padding: '4px 12px', border: `2px solid ${car.status === 'Disponível' ? '#10B981' : car.status === 'Reservado' ? '#DC2626' : '#6B7280'}`, color: car.status === 'Disponível' ? '#10B981' : car.status === 'Reservado' ? '#DC2626' : '#6B7280', fontWeight: '700', fontSize: '10px' }}>
          {car.status?.toUpperCase()}
        </div>
      </div>

      {/* Main Content - Two columns */}
      <div style={{ display: 'flex', gap: '8mm' }}>
        {/* Left - Image */}
        <div style={{ width: '70mm', height: '50mm', background: '#f5f5f5', overflow: 'hidden', flexShrink: 0 }}>
          <img src={mainImage} alt={`${car.brand} ${car.model}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>

        {/* Right - Main Info */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '22px', fontWeight: '700', color: '#111', marginBottom: '2mm' }}>{car.brand} {car.model}</div>
          <div style={{ fontSize: '26px', fontWeight: '800', color: '#DC2626', marginBottom: '4mm' }}>{formatPrice(car.price)}</div>
          
          {/* Specs inline */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '3mm', background: '#f8f8f8', padding: '4mm' }}>
            <div><span style={{ fontSize: '9px', color: '#888' }}>ANO</span><div style={{ fontSize: '14px', fontWeight: '600' }}>{car.year}</div></div>
            <div><span style={{ fontSize: '9px', color: '#888' }}>KM</span><div style={{ fontSize: '14px', fontWeight: '600' }}>{formatMileage(car.mileage)}</div></div>
            <div><span style={{ fontSize: '9px', color: '#888' }}>COMBUSTÍVEL</span><div style={{ fontSize: '14px', fontWeight: '600' }}>{car.fuel}</div></div>
            <div><span style={{ fontSize: '9px', color: '#888' }}>CAIXA</span><div style={{ fontSize: '14px', fontWeight: '600' }}>{car.transmission}</div></div>
          </div>
        </div>
      </div>

      {/* Extras */}
      {car.extras && car.extras.length > 0 && (
        <div style={{ marginTop: '6mm' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', marginBottom: '3mm', color: '#333', borderBottom: '1px solid #ddd', paddingBottom: '2mm' }}>EQUIPAMENTOS</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1mm 3mm' }}>
            {car.extras.map((extra, i) => (
              <div key={i} style={{ fontSize: '9px', color: '#444', padding: '1mm 0' }}>✓ {extra}</div>
            ))}
          </div>
        </div>
      )}

      {/* Description if exists */}
      {car.description && (
        <div style={{ marginTop: '5mm', padding: '3mm', background: '#f8f8f8', fontSize: '10px', color: '#555', lineHeight: '1.4' }}>
          {car.description.substring(0, 300)}{car.description.length > 300 ? '...' : ''}
        </div>
      )}

      {/* Footer */}
      <div style={{ position: 'absolute', bottom: '10mm', left: '10mm', right: '10mm', paddingTop: '3mm', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#666' }}>
        <span>{settings?.website || 'icardetail.pt'}</span>
        <span>{settings?.phone || '919 714 805'} | {settings?.address_full || 'R. 25 de Abril 908, Agrela'}</span>
      </div>
    </div>
  );
});
PrintContent.displayName = 'PrintContent';

// Image Lightbox Component
const ImageLightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 text-white hover:text-red-500 z-10">
        <X size={32} />
      </button>
      
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-red-600 flex items-center justify-center transition-colors">
        <ChevronLeft size={28} className="text-white" />
      </button>
      
      <img 
        src={images[currentIndex]} 
        alt="" 
        className="max-w-[90vw] max-h-[90vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-red-600 flex items-center justify-center transition-colors">
        <ChevronRight size={28} className="text-white" />
      </button>
      
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </div>
  );
};

const CarDetailPage = () => {
  const { id } = useParams();
  const { settings } = useSettings();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const printRef = useRef(null);

  useEffect(() => { fetchCar(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCar = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/cars/${id}`);
      setCar(response.data);
    } catch (error) {
      console.error('Error fetching car:', error);
    } finally {
      setLoading(false);
    }
  };

  const reactToPrintFn = useReactToPrint({ contentRef: printRef, documentTitle: car ? `Ficha_${car.brand}_${car.model}` : 'Ficha_Viatura' });
  const handlePrint = useCallback(() => { if (printRef.current) reactToPrintFn(); }, [reactToPrintFn]);

  const getStatusBadge = (status) => ({ 'Disponível': 'badge-available', 'Reservado': 'badge-reserved', 'Vendido': 'badge-sold' }[status] || 'badge-available');
  const formatPrice = (price) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(price);
  const formatMileage = (mileage) => new Intl.NumberFormat('pt-PT').format(mileage);

  const defaultImage = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800';
  const images = car?.images?.length > 0 ? car.images.map(resolveImageUrl) : [defaultImage];

  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  if (loading) return <div className="min-h-screen bg-[#090A0F]"><Header /><div className="pt-32 flex items-center justify-center"><p className="text-zinc-400">A carregar...</p></div></div>;
  if (!car) return <div className="min-h-screen bg-[#090A0F]"><Header /><div className="pt-32 flex flex-col items-center justify-center"><p className="text-zinc-400 mb-4">Viatura não encontrada</p><Link to="/" className="text-red-500 hover:text-red-400">Voltar ao início</Link></div></div>;

  const youtubeUrl = getYouTubeEmbedUrl(car.youtube_url);

  const fuelMap = { 'Gasóleo': 'diesel', 'Gasolina': 'gasolina', 'Diesel': 'diesel', 'Híbrido': 'híbrido', 'Elétrico': 'elétrico' };
  const fuelSeo = fuelMap[car?.fuel] || car?.fuel?.toLowerCase() || '';
  const seoTitle = `${car.brand} ${car.model} ${car.year} | ${formatPrice(car.price)} | ICarDetail`;
  const seoDesc = `${car.brand} ${car.model} de ${car.year} com ${formatMileage(car.mileage)} km, ${fuelSeo}. ${car.status === 'Disponível' ? 'Disponível' : car.status} na ICarDetail em Agrela. Financiamento disponível.`;
  const seoOgDesc = `${formatPrice(car.price)} | ${car.year} | ${formatMileage(car.mileage)} km | ${fuelSeo} | Stand ICarDetail, Agrela`;
  const carUrl = `${window.location.origin}/viatura/${car.id}`;

  const vehicleSchema = {
    "@context": "https://schema.org",
    "@type": "Car",
    "name": `${car.brand} ${car.model}`,
    "brand": { "@type": "Brand", "name": car.brand },
    "model": car.model,
    "vehicleModelDate": String(car.year),
    "mileageFromOdometer": {
      "@type": "QuantitativeValue",
      "value": car.mileage,
      "unitCode": "KMT"
    },
    "fuelType": car.fuel,
    "vehicleTransmission": car.transmission,
    "vehicleConfiguration": car.transmission,
    "color": "",
    "itemCondition": "https://schema.org/UsedCondition",
    "offers": {
      "@type": "Offer",
      "price": car.price,
      "priceCurrency": "EUR",
      "availability": car.status === 'Vendido' ? "https://schema.org/SoldOut" : "https://schema.org/InStock",
      "seller": {
        "@type": "AutoDealer",
        "name": "ICarDetail Automóveis",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "R. 25 de Abril 908",
          "addressLocality": "Agrela",
          "addressRegion": "Santo Tirso",
          "postalCode": "4825-026",
          "addressCountry": "PT"
        }
      }
    },
    "image": images[0],
    "url": carUrl,
    "description": car.description || seoDesc
  };

  return (
    <div className="min-h-screen bg-[#090A0F]">
      <Header />
      
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="keywords" content={`${car.brand} ${car.model}, carros usados, ${car.brand} usado, carros usados Agrela, carros usados Santo Tirso, stand automóveis, ${fuelSeo}`} />
        <meta property="og:title" content={`${car.brand} ${car.model} ${car.year} | ${formatPrice(car.price)}`} />
        <meta property="og:description" content={seoOgDesc} />
        <meta property="og:image" content={images[0]} />
        <meta property="og:url" content={carUrl} />
        <meta property="og:type" content="product" />
        <meta property="og:site_name" content="ICarDetail Automóveis" />
        <meta property="og:locale" content="pt_PT" />
        <meta property="product:price:amount" content={String(car.price)} />
        <meta property="product:price:currency" content="EUR" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${car.brand} ${car.model} ${car.year} | ${formatPrice(car.price)}`} />
        <meta name="twitter:description" content={seoDesc} />
        <meta name="twitter:image" content={images[0]} />
        <link rel="canonical" href={carUrl} />
        <script type="application/ld+json">{JSON.stringify(vehicleSchema)}</script>
      </Helmet>
      
      {/* Hidden print component */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}><PrintContent ref={printRef} car={car} settings={settings} /></div>
      
      {/* Lightbox */}
      {lightboxOpen && (
        <ImageLightbox 
          images={images} 
          currentIndex={currentImageIndex} 
          onClose={() => setLightboxOpen(false)}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}
      
      {/* Share Modal */}
      {shareOpen && <ShareCarCard car={car} onClose={() => setShareOpen(false)} />}

      <main className="pt-28 pb-20 px-4 sm:px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Back & Print */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors" data-testid="back-to-list-btn">
              <ArrowLeft size={20} /> Voltar ao stock
            </Link>
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button onClick={() => setShareOpen(true)} className="flex-1 sm:flex-none bg-zinc-800 hover:bg-zinc-700 text-white px-4 sm:px-6 py-2 inline-flex items-center justify-center gap-2 border border-zinc-700 text-sm" data-testid="share-car-btn">
                <Share2 size={16} /> Partilhar
              </Button>
              <Button onClick={handlePrint} className="flex-1 sm:flex-none bg-red-600 hover:bg-red-700 text-white px-4 sm:px-6 py-2 inline-flex items-center justify-center gap-2 text-sm" data-testid="print-car-btn">
                <Printer size={16} /> Imprimir
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Images */}
            <div className="space-y-4 animate-fade-in overflow-hidden" data-testid="car-gallery">
              <div className="relative aspect-[16/10] bg-[#12141D] overflow-hidden group cursor-pointer" onClick={() => setLightboxOpen(true)}>
                <img src={images[currentImageIndex]} alt={`${car.brand} ${car.model}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                
                {/* Zoom indicator */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <ZoomIn size={40} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                
                {images.length > 1 && (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors" data-testid="gallery-prev-btn"><ChevronLeft size={24} className="text-white" /></button>
                    <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 flex items-center justify-center transition-colors" data-testid="gallery-next-btn"><ChevronRight size={24} className="text-white" /></button>
                  </>
                )}
                
                {/* Status Badge */}
                <div className="absolute top-4 left-4"><span className={`px-4 py-2 text-sm font-bold uppercase ${getStatusBadge(car.status)}`}>{car.status}</span></div>
                
                {/* Promotion Badge */}
                {car.promotion && (
                  <div className="absolute top-0 right-0 w-0 h-0 border-t-[100px] border-t-red-600 border-l-[100px] border-l-transparent">
                    <span className="absolute -top-[85px] right-[8px] text-white text-sm font-bold transform rotate-45 whitespace-nowrap">{car.promotion}</span>
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide sm:grid sm:grid-cols-5 sm:gap-2 sm:overflow-visible sm:pb-0">
                  {images.map((img, index) => (
                    <button key={`thumb-${index}`} onClick={() => setCurrentImageIndex(index)} className={`flex-shrink-0 w-16 h-16 sm:w-auto sm:h-auto sm:aspect-square overflow-hidden border-2 transition-colors ${index === currentImageIndex ? 'border-red-500' : 'border-transparent hover:border-zinc-600'}`} data-testid={`gallery-thumb-${index}`}>
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* YouTube */}
              {youtubeUrl && (
                <div className="aspect-video bg-[#12141D]" data-testid="car-video">
                  <iframe src={youtubeUrl} title="Video da Viatura" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" />
                </div>
              )}
            </div>

            {/* Details */}
            <div className="animate-fade-in-delay-1">
              <div className="mb-6">
                <p className="uppercase tracking-[0.2em] text-xs font-semibold text-zinc-500 mb-2">{car.brand}</p>
                <h1 className="text-4xl sm:text-5xl tracking-tight font-medium text-white mb-4" data-testid="car-title">{car.model}</h1>
                <p className="text-3xl font-semibold text-red-500" data-testid="car-price">{formatPrice(car.price)}</p>
              </div>

              {/* Specs */}
              <div className="grid grid-cols-2 gap-4 p-6 bg-[#12141D] border border-zinc-800 mb-8" data-testid="car-specs">
                <div className="flex items-center gap-3"><Calendar size={24} className="text-red-500" /><div><p className="text-zinc-500 text-xs">Ano</p><p className="text-white font-medium">{car.year}</p></div></div>
                <div className="flex items-center gap-3"><Gauge size={24} className="text-red-500" /><div><p className="text-zinc-500 text-xs">Quilómetros</p><p className="text-white font-medium">{formatMileage(car.mileage)} km</p></div></div>
                <div className="flex items-center gap-3"><Fuel size={24} className="text-red-500" /><div><p className="text-zinc-500 text-xs">Combustível</p><p className="text-white font-medium">{car.fuel}</p></div></div>
                <div className="flex items-center gap-3"><Settings2 size={24} className="text-red-500" /><div><p className="text-zinc-500 text-xs">Caixa</p><p className="text-white font-medium">{car.transmission}</p></div></div>
              </div>

              {/* Description */}
              {car.description && (
                <div className="mb-8" data-testid="car-description">
                  <h3 className="text-lg font-medium text-white mb-3">Descrição</h3>
                  <p className="text-zinc-400 leading-relaxed whitespace-pre-line">{car.description}</p>
                </div>
              )}

              {/* Extras */}
              {car.extras && car.extras.length > 0 && (
                <div className="mb-8" data-testid="car-extras">
                  <h3 className="text-lg font-medium text-white mb-4">Equipamentos</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {car.extras.map((extra, index) => (
                      <div key={extra} className="flex items-center gap-2 text-zinc-300"><Check size={16} className="text-green-500 flex-shrink-0" /><span className="text-sm">{extra}</span></div>
                    ))}
                  </div>
                </div>
              )}

              {/* Contact */}
              <ContactForm carId={car.id} carInfo={`${car.brand} ${car.model} (${car.year})`} />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CarDetailPage;
